import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/shared/state/authstore";
import { toast } from "sonner";
import { ensureFreshToken } from "./ensureFreshToken";
import { redirectToLogin } from "@/shared/lib/loginRedirect";
import { explainBillingRefusal } from "./billingToast";
import { explainEmploymentRevoked } from "./employmentRevokedToast";

export const http = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    withCredentials: true,
});

// Add a request interceptor to include the auth token in headers
http.interceptors.request.use(async (config) => {
    const authStore = useAuthStore.getState();
    const accessToken = authStore.accessToken;

    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }

    const pinned = config as InternalAxiosRequestConfig & { _accountId?: number };
    pinned._accountId ??= authStore.accountId ?? undefined;
    if (pinned._accountId) {
        config.headers['X-Account-Id'] = pinned._accountId;
    }

    config.headers['X-Portal'] = 'gms';

    return config;
});

http.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/token/refresh')) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                const accessToken = await ensureFreshToken(true);
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return http(originalRequest);
            } catch (refreshError) {
                if ((refreshError as AxiosError).response?.status === 401) {
                    useAuthStore.getState().clearAuth();
                    toast.error("Session expired. Please login again.");
                    redirectToLogin();
                } else if ((refreshError as AxiosError).response || (refreshError as AxiosError).request) {
                    toast.error("Unable to reach the server. Please check your connection and try again.");
                }
                return Promise.reject(refreshError);
            }
        }

        // Handle other error scenarios
        const skipToast = (originalRequest as InternalAxiosRequestConfig & { _skipErrorToast?: boolean })._skipErrorToast;

        if (error.response) {
            const status = error.response.status;

            // Plan-caused refusals (402 capacity, 403 past-due/suspended) get their own explanation
            // instead of the generic message for their status code.
            if (explainBillingRefusal(error, { silent: !!skipToast })) {
                return Promise.reject(error);
            }

            // An ended employment is not a missing permission. It gets its own explanation, and the
            // account is let go of here, not at the call site, so every request that notices does it once.
            if (explainEmploymentRevoked(error, { silent: !!skipToast })) {
                return Promise.reject(error);
            }

            if ((status === 429 || status === 503) && originalRequest.url?.includes('/auth/')) {
                return Promise.reject(error);
            }

            if (!skipToast) {
                // Hardcoded friendly messages - ignoring backend text
                const errorMessages: Record<number, string> = {
                    400: "Invalid request. Please check your data and try again.",
                    401: "Your session has expired. Please log in again.",
                    403: "You do not have permission to perform this action.",
                    404: "The requested resource was not found.",
                    422: "Please check your input and try again.",
                    500: "An internal server error occurred. Please try again later."
                };

                const message = errorMessages[status] || `Something went wrong. Please try again. (Code: ${status})`;
                toast.error(message);
            }
        } else if (error.request && !skipToast) {
            toast.error("Unable to connect to the server. Please check your internet connection.");
        } else if (!skipToast) {
            toast.error("An unexpected error occurred.");
        }

        return Promise.reject(error);
    }
);
